import axios from 'axios';
import Constants from '../config/constants';
import store from '../store';
import { formDataRequest, postRequest } from '../libs/AxiosService.util';

export const getHost = () => {
    if (process.env.REACT_APP_HOST) {
        return process.env.REACT_APP_HOST.replace(/\/+$/, '');
    }
    const appUrl = Constants.DEFAULT_APP_URL || process.env.REACT_APP_API_URL || '';
    try {
        if (appUrl.startsWith('http://') || appUrl.startsWith('https://')) {
            return new URL(appUrl).origin;
        }
    } catch (e) {}
    return (appUrl || '').replace(/\/api\/.*$/, '').replace(/\/+$/, '');
};

export const getThemeByDomainUrl = () => {
    if (process.env.REACT_APP_HOST) {
        return `${process.env.REACT_APP_HOST.replace(/\/+$/, '')}/api/admin/clients/theme-by-domain`;
    }
    const appUrl = Constants.DEFAULT_APP_URL || process.env.REACT_APP_API_URL || '';
    if (appUrl.includes('/api/')) {
        const base = appUrl.substring(0, appUrl.indexOf('/api/') + 5);
        return `${base.replace(/\/+$/, '')}/admin/clients/theme-by-domain`;
    }
    const host = getHost();
    return `${host}/api/admin/clients/theme-by-domain`;
};

let themePromise = null;
let lastCallTime = 0;

export const normalizeThemeData = (raw) => {
    if (!raw || typeof raw !== 'object') return {};

    let data = raw;
    if (data.data) {
        data = data.data;
    }
    if (data.response_obj) {
        data = data.response_obj;
    }

    const clientTheme = (data.theme && typeof data.theme === 'object') ? data.theme : {};
    const nestedTheme = (clientTheme.theme && typeof clientTheme.theme === 'object') ? clientTheme.theme : {};

    const merged = {
        ...data,
        ...clientTheme,
        ...nestedTheme,
    };

    const display_logo = merged.display_logo || merged.logo || merged.logo_url || merged.logo_image || '';
    const side_drawer_icon = merged.side_drawer_icon || merged.fav_icon || merged.favicon || merged.favicon_url || merged.favIcon || merged.icon || '';
    const fav_icon = merged.fav_icon || merged.favicon || merged.favicon_url || merged.favIcon || merged.side_drawer_icon || '';
    const background_image = merged.background_image || merged.login_background || merged.backgroundImage || merged.bg_image || merged.image || '';

    return {
        ...merged,
        display_logo,
        logo: display_logo,
        logo_url: display_logo,
        side_drawer_icon,
        icon: side_drawer_icon,
        fav_icon,
        favicon: fav_icon,
        background_image,
        login_background: background_image,
        theme: {
            ...nestedTheme,
            display_logo,
            side_drawer_icon,
            fav_icon,
            background_image,
        }
    };
};

export const applyThemeToUI = (rawData) => {
    if (!rawData) return;
    const themeData = normalizeThemeData(rawData);

    if (themeData.background_image) {
        document.documentElement.style.setProperty('--login-bg-image', `url("${themeData.background_image}")`);
        document.documentElement.style.setProperty('--login-overlay-image', 'none');
    }

    if (themeData.display_logo) {
        document.documentElement.style.setProperty('--login-logo', `url("${themeData.display_logo}")`);
    }

    const icon = themeData.side_drawer_icon || themeData.fav_icon;
    if (icon) {
        document.documentElement.style.setProperty('--sidebar-favicon', `url("${icon}")`);
        document.documentElement.style.setProperty('--sidebar-icon', `url("${icon}")`);
    }

    const favicon = themeData.fav_icon;
    if (favicon && typeof document !== 'undefined') {
        let link = document.querySelector("link[rel='shortcut icon']") || document.querySelector("link[rel~='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'shortcut icon';
            document.getElementsByTagName('head')[0].appendChild(link);
        }
        link.href = favicon;
    }
};

export const clearThemeFromUI = () => {
    if (typeof document === 'undefined') return;
    document.documentElement.style.removeProperty('--login-bg-image');
    document.documentElement.style.removeProperty('--login-overlay-image');
    document.documentElement.style.removeProperty('--login-logo');
    document.documentElement.style.removeProperty('--sidebar-favicon');
    document.documentElement.style.removeProperty('--sidebar-icon');
    const link = document.querySelector("link[rel='shortcut icon']") || document.querySelector("link[rel~='icon']");
    if (link) {
        link.href = '/favicon.png';
    }
};

export const getCachedTheme = (domain) => {
    try {
        if (typeof localStorage === 'undefined') return null;
        const currentDomain = domain || ((typeof window !== 'undefined' && window.location.hostname) ? window.location.hostname : '');
        const domainKey = currentDomain ? `client_theme_${currentDomain}` : null;
        const cachedStr = (domainKey && localStorage.getItem(domainKey)) ||
            localStorage.getItem('domain_theme_data') ||
            localStorage.getItem('domainTheme');
        return cachedStr ? JSON.parse(cachedStr) : null;
    } catch (e) {
        return null;
    }
};

export const setCachedTheme = (themeData, domain) => {
    try {
        if (typeof localStorage === 'undefined' || !themeData) return;
        const currentDomain = domain || ((typeof window !== 'undefined' && window.location.hostname) ? window.location.hostname : '');
        const jsonStr = JSON.stringify(themeData);
        if (currentDomain) {
            localStorage.setItem(`client_theme_${currentDomain}`, jsonStr);
        }
        localStorage.setItem('domain_theme_data', jsonStr);
        localStorage.setItem('domainTheme', jsonStr);
    } catch (e) {
        console.warn('Failed to cache theme to localStorage:', e);
    }
};

export const clearCachedTheme = (domain) => {
    try {
        if (typeof localStorage === 'undefined') return;
        const currentDomain = domain || ((typeof window !== 'undefined' && window.location.hostname) ? window.location.hostname : '');
        if (currentDomain) {
            localStorage.removeItem(`client_theme_${currentDomain}`);
        }
        localStorage.removeItem('domain_theme_data');
        localStorage.removeItem('domainTheme');
    } catch (e) {
        console.warn('Failed to clear cached theme from localStorage:', e);
    }
};

try {
    const cachedTheme = getCachedTheme();
    if (cachedTheme) {
        applyThemeToUI(cachedTheme);
    }
} catch (e) {}

const handleThemeSuccess = (rawResponse, targetDomain) => {
    const raw = rawResponse?.response_obj || rawResponse || {};
    const normalized = normalizeThemeData(raw);

    applyThemeToUI(normalized);
    setCachedTheme(normalized, targetDomain);

    if (store && store.dispatch) {
        store.dispatch({
            type: 'APP_SETTINGS_THEME_BY_DOMAIN',
            payload: normalized,
        });
        store.dispatch({
            type: 'GET_THEME_BY_DOMAIN',
            payload: normalized,
        });
    }

    return { error: false, data: normalized, raw: rawResponse };
};

const handleThemeFailure = (targetDomain, errorMsg, responseCode, raw) => {
    clearCachedTheme(targetDomain);
    clearThemeFromUI();

    if (store && store.dispatch) {
        store.dispatch({
            type: 'APP_SETTINGS_THEME_BY_DOMAIN',
            payload: null,
        });
        store.dispatch({
            type: 'GET_THEME_BY_DOMAIN',
            payload: null,
        });
    }

    return {
        error: true,
        message: errorMsg || 'Domain theme not found or inactive',
        response_code: responseCode,
        raw
    };
};

export async function serviceGetThemeByDomain(params = {}) {
    const now = Date.now();
    if (themePromise && now - lastCallTime < 800) {
        return themePromise;
    }
    lastCallTime = now;

    const url = getThemeByDomainUrl();
    const currentDomain = (typeof window !== 'undefined' && window.location.hostname) ? window.location.hostname : '';
    const payload = {
        ...(params || {}),
    };
    if (!payload.domain && currentDomain && currentDomain !== 'localhost') {
        payload.domain = currentDomain;
    }
    const targetDomain = payload.domain || currentDomain || '';

    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('jwt_token') : null;
    const headers = {};
    if (token) {
        headers['Authorization'] = `${token}`;
    }

    themePromise = (async () => {
        try {
            const res = await axios.post(url, payload, { headers });
            if (res?.data?.response_code === 1) {
                return handleThemeSuccess(res.data, targetDomain);
            } else {
                return handleThemeFailure(
                    targetDomain,
                    res?.data?.response_message || 'Domain theme not found or invalid',
                    res?.data?.response_code,
                    res?.data
                );
            }
        } catch (err) {
            if (err?.response?.status === 405) {
                try {
                    const getRes = await axios.get(url, { params: payload, headers });
                    if (getRes?.data?.response_code === 1) {
                        return handleThemeSuccess(getRes.data, targetDomain);
                    } else {
                        return handleThemeFailure(
                            targetDomain,
                            getRes?.data?.response_message || 'Domain theme not found or invalid',
                            getRes?.data?.response_code,
                            getRes?.data
                        );
                    }
                } catch (getErr) {
                    console.error('Error calling theme-by-domain (GET):', getErr);
                    return handleThemeFailure(
                        targetDomain,
                        getErr?.response?.data?.response_message || getErr?.message,
                        getErr?.response?.data?.response_code,
                        getErr?.response?.data
                    );
                }
            }
            console.error('Error calling theme-by-domain:', err);
            return handleThemeFailure(
                targetDomain,
                err?.response?.data?.response_message || err?.message,
                err?.response?.data?.response_code,
                err?.response?.data
            );
        } finally {
            setTimeout(() => {
                themePromise = null;
            }, 800);
        }
    })();

    return themePromise;
}

export async function serviceCreateClient(params) {
    return await formDataRequest('clients/create', params);
}

export async function serviceUpdateClient(params) {
    return await formDataRequest('clients/update', params);
}

export async function serviceFetchClient(params) {
    return await postRequest("clients", params);
}

export async function serviceClientDetail(params) {
    return await postRequest("clients/detail", params);
}
export async function serviceDeleteClient(params) {
    return await postRequest("clients/delete", params);
}

export async function serviceGetClientsProgram(params) {
    return await postRequest("programs", params);
}
export async function serviceClientIsExist (params) {  
    return await postRequest('clients/check', params);
}

